import type { SkyDataGridColumn } from "@skypointcloud/skypoint-ui";

/**
 * The one table convention for list screens (rules.md §4; client/AGENTS.md "Page layout").
 *
 * Every `SkyDataGrid` is unbordered: row dividers only, the Fluent 2 data grid. A list whose
 * rows are loaded in full pages itself with the setting below; a list the server pages keeps its
 * own control (round 4: `CursorPager` on the Runs list, a connection's imports and a run's events;
 * the audit trail's Previous/Next). Since skypoint-ui 2.17.0
 * the grid clamps its page when the data shrinks, so a filter never strands it on an empty page.
 */
export const LIST_PAGE_SIZE = 20;

export const LIST_PAGE_SIZE_OPTIONS: number[] = [10, 20, 50];

/** Spread onto a `SkyDataGrid` holding a fully loaded list: `{...LIST_GRID_PAGINATION}`. */
export const LIST_GRID_PAGINATION: {
  pagination: boolean;
  pageSize: number;
  pageSizeOptions: number[];
} = {
  pagination: true,
  pageSize: LIST_PAGE_SIZE,
  pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
};

/** The row-selection column `SkyDataGrid` adds for single or multiple selection (`w-12`). */
const GRID_SELECTION_COLUMN = 48;

/**
 * A `table-fixed` grid with pixel columns beside percentage ones (fixes waves 3 and 4): the
 * percentages share what the pixel columns leave, so without a floor a narrow pane squeezes every
 * one of them, and a header runs into its neighbour. Each percent column's share is set in
 * proportion to its minimum (`minimums`, keyed by column key; `defaultMinimum` otherwise), so the
 * smallest table that gives each its minimum is the pixel columns plus the minimums; that width,
 * rounded up to 8 px, is the floor. Below it the grid's region scrolls sideways.
 *
 * Tailwind's preflight makes every cell border-box, so a width here already holds the header's
 * 32 px padding: a minimum is the header text plus 32, measured in Chromium, never in jsdom.
 * The floor needs a CSS variable on an ancestor (Tailwind cannot see a class built at runtime).
 */
export function pixelColumnLayout<T>(
  columns: SkyDataGridColumn<T>[],
  options: {
    minimums: Readonly<Record<string, number>>;
    defaultMinimum?: number;
    /** The grid shows a selection column. */
    selection: boolean;
  },
): { columns: SkyDataGridColumn<T>[]; floor: number } {
  const minimumOf = (column: SkyDataGridColumn<T>) =>
    options.minimums[String(column.key)] ?? options.defaultMinimum ?? 96;
  const isPercent = (column: SkyDataGridColumn<T>) => column.width?.endsWith("%") ?? false;
  let fixed = options.selection ? GRID_SELECTION_COLUMN : 0;
  let minimums = 0;
  for (const column of columns) {
    if (column.width?.endsWith("px")) fixed += Number.parseFloat(column.width);
    if (isPercent(column)) minimums += minimumOf(column);
  }
  return {
    columns: columns.map((column) =>
      isPercent(column)
        ? { ...column, width: `${((minimumOf(column) / minimums) * 100).toFixed(2)}%` }
        : column,
    ),
    floor: Math.ceil((fixed + minimums) / 8) * 8,
  };
}

/**
 * A grid shows its Actions column only when the reader can act on a row (#1196): a role with no
 * action on any row would otherwise see a header over empty cells, and the column would take width
 * the other headers need. Spread the result at the end of the column list:
 * `[...columns, ...actionsColumnWhen(canAct, actionsColumn)]`.
 */
export function actionsColumnWhen<T>(
  hasAction: boolean,
  column: SkyDataGridColumn<T>,
): SkyDataGridColumn<T>[] {
  return hasAction ? [column] : [];
}
